import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Search, Download, Crown, Phone, Mail } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import GuestIdThumbs from "./GuestIdThumbs";

const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");

export default function GuestDatabase() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setData(null);
    api.get("/pms/guests", { params: { q } }).then(({ data: d }) => setData(d))
      .catch((e) => toast.error(errMsg(e)));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const head = ["Guest", "Mobile", "Email", "Country", "Stays", "Nights", "Spend (INR)", "Last stay", "Channels", "VIP"];
    const lines = [head.join(",")];
    data.guests.forEach((g) => lines.push([`"${g.guest}"`, g.mobile, g.email, g.country, g.stays, g.nights, g.spend, g.last_stay, `"${g.channels.join('|')}"`, g.vip ? "Yes" : ""].join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `atiti-guests-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success(`Exported ${data.guests.length} guests`);
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  return (
    <div className="reveal space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-slate-50 rounded-full px-4 py-2 border border-slate-100">
          <Search size={14} className="text-slate-400" />
          <input data-testid="guest-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Quick search by name, email, mobile…" className="w-full bg-transparent text-sm focus:outline-none" />
        </div>
        <span className="text-xs text-slate-500 font-semibold"><Crown size={12} className="inline text-amber-500 -mt-0.5" /> {data.vip_count} VIP · {data.count} total</span>
        <button data-testid="guest-export" onClick={exportCsv} className="text-xs px-4 py-2 border border-slate-200 rounded-full hover:border-slate-400 flex items-center gap-1.5"><Download size={12} /> Export</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-slate-100 bg-slate-50/60">
          <span className="font-display font-bold text-slate-900 text-sm">Guest Database</span>
          <span className="hidden sm:inline text-[11px] text-slate-500 font-semibold">Derived from every booking across every property</span>
        </div>
        {/* Desktop / tablet-landscape table (≥lg) */}
        <div className="hidden lg:block overflow-x-auto max-h-[70vh] overflow-y-auto scroll-hint-right">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left px-5 py-3 font-semibold">Guest Name</th>
                <th className="text-left px-3 py-3 font-semibold">Country</th>
                <th className="text-left px-3 py-3 font-semibold">Email</th>
                <th className="text-left px-3 py-3 font-semibold">Mobile</th>
                <th className="text-right px-3 py-3 font-semibold">Stays / Nights</th>
                <th className="text-right px-3 py-3 font-semibold">Total spend</th>
                <th className="text-left px-3 py-3 font-semibold">Last stay</th>
                <th className="text-left px-3 py-3 font-semibold">ID Photos</th>
                <th className="text-right px-3 py-3 font-semibold">VIP</th>
                <th className="text-right px-5 py-3 font-semibold">ID</th>
              </tr>
            </thead>
            <tbody>
              {data.guests.map((g, i) => (
                <tr key={g.guest + g.mobile + i} data-testid={`guest-row-${i}`} className="border-b border-slate-50 hover:bg-slate-50/70">
                  <td className="px-5 py-3">
                    <p className="font-semibold text-slate-800">{g.guest}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{g.channels.join(" · ") || "—"}</p>
                  </td>
                  <td className="px-3 py-3 text-slate-500 text-xs">{g.country || "—"}</td>
                  <td className="px-3 py-3 text-slate-500 text-xs truncate max-w-[220px]" title={g.email}>{g.email || "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-600">{g.mobile || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-600 text-xs">{g.stays} · {g.nights}n</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-700">{fmt(g.spend)}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-500">{g.last_stay || "—"}</td>
                  <td className="px-3 py-3">
                    <GuestIdThumbs paths={g.id_photos || []} max={3} guestName={g.guest} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    {g.vip ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 inline-flex items-center gap-1"><Crown size={10} /> VIP</span> : <span className="text-slate-300 text-[10px]">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <IdDownloadBtn i={i} g={g} />
                  </td>
                </tr>
              ))}
              {data.guests.length === 0 && (
                <tr><td colSpan="10" className="px-6 py-12 text-center text-sm text-slate-400">No guests match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Card view (<lg) — every guest is a tap-large card */}
        <div className="lg:hidden divide-y divide-slate-100 max-h-[75vh] overflow-y-auto">
          {data.guests.length === 0 && <p className="px-6 py-12 text-center text-sm text-slate-400">No guests match your search.</p>}
          {data.guests.map((g, i) => (
            <div key={g.guest + g.mobile + i} data-testid={`guest-card-${i}`} className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 truncate">{g.guest}</p>
                    {g.vip && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 inline-flex items-center gap-0.5"><Crown size={10} /> VIP</span>}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{g.channels.join(" · ") || "—"} {g.country ? `· ${g.country}` : ""}</p>
                  <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 font-mono flex-wrap">
                    {g.mobile && <span className="inline-flex items-center gap-1"><Phone size={10} /> {g.mobile}</span>}
                    {g.email && <span className="inline-flex items-center gap-1 truncate max-w-[180px]"><Mail size={10} /> {g.email}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold text-slate-800">{fmt(g.spend)}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{g.stays} stay · {g.nights}n</div>
                  {g.last_stay && <div className="text-[10px] text-slate-400 font-mono mt-0.5">last {g.last_stay}</div>}
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <GuestIdThumbs paths={g.id_photos || []} max={3} guestName={g.guest} />
                <IdDownloadBtn i={i} g={g} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Phase 2 (2026-02): extracted from the desktop table so the mobile card
// view can reuse the same bundled-IDs download button without duplicating
// the download logic (single ID → direct file, multiple → ZIP).
function IdDownloadBtn({ i, g }) {
  return (
    <button
      data-testid={`guest-ids-download-${i}`}
      onClick={async () => {
        try {
          const res = await api.get("/pms/guests-ids-download", {
            params: { guest: g.guest, mobile: g.mobile || "", email: g.email || "" },
            responseType: "blob",
          });
          const ct = res.headers?.["content-type"] || "";
          const disp = res.headers?.["content-disposition"] || "";
          const m = disp.match(/filename="?([^";]+)"?/);
          const filename = m ? m[1] : (ct.includes("zip") ? `${g.guest}-ids.zip` : `${g.guest}-id`);
          const url = URL.createObjectURL(res.data);
          const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
          URL.revokeObjectURL(url);
          toast.success("ID(s) downloaded");
        } catch (e) {
          toast.error(errMsg(e));
        }
      }}
      className="text-[10px] font-semibold px-2 py-1 rounded-full border border-slate-200 hover:border-[#4F46E5] hover:text-[#4F46E5] inline-flex items-center gap-1"
      title="Download uploaded ID(s) — bundled as ZIP if multiple">
      <Download size={11} /> IDs
    </button>
  );
}
